/**
 * Pillar 5 — Morning Roll Call banner.
 *
 * Surfaces on the Operations Dashboard for the chosen project. Tells the
 * superintendent at 5:30 AM, before any digging:
 *
 *    "2 new revisions · 1 critical change · 3 foremen pending"
 *
 * One tap → Revision War Room.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sunrise, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ImpactChip from "@/components/war_room/ImpactChip";


export default function MorningRollCall({ projectId, testid = "morning-roll-call" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // R-09 (independent recheck, 2026-10-01) — same bug class as the
  // dashboard rows below it: a failed fetch set `data` to `null`, and
  // `if (!data) return null` made the ENTIRE banner silently vanish —
  // on a page titled "What needs attention today", an absent banner
  // reads as "nothing to report" just as easily as a false all-clear
  // would. Track the failure explicitly instead of overloading `null`.
  const [failed, setFailed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    if (!projectId) { setData(null); setFailed(false); return; }
    let alive = true;
    setLoading(true);
    setFailed(false);
    api.get(`/projects/${projectId}/morning-roll-call`).then((r) => {
      if (alive) setData(r.data);
    }).catch(() => { if (alive) { setData(null); setFailed(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, retryTick]);

  if (!projectId) return null;
  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text-3)] text-sm" data-testid={`${testid}-loading`}>
        <Loader2 className="animate-spin" size={14} /> Reading the morning roll call…
      </div>
    );
  }
  if (failed) {
    return (
      <div
        className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[var(--st-pending,#f59e0b)]/60 bg-[var(--fop-surface)] text-[var(--fop-text)]"
        data-testid={testid}
        data-unavailable="true"
      >
        <Sunrise size={20} style={{ color: "var(--st-pending, #f59e0b)" }} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Morning roll call</div>
          <div className="text-sm font-semibold" style={{ color: "var(--st-pending, #f59e0b)" }}>
            Couldn't load — status unknown, not verified clear
          </div>
        </div>
        <button
          type="button"
          onClick={() => setRetryTick((t) => t + 1)}
          className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] shrink-0"
          data-testid={`${testid}-retry`}
        >
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const allClear = data.new_revisions === 0 && data.critical_changes === 0
                   && data.high_changes === 0 && data.foremen_pending === 0;

  return (
    <Link
      to={data.war_room_url}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
        allClear
          ? "border-[var(--fop-border)] bg-[var(--fop-surface)] text-[var(--fop-text-2)] hover:border-[var(--fop-border-2)]"
          : "border-[var(--fop-primary)]/60 bg-[var(--fop-primary-soft)] text-[var(--fop-text)] hover:bg-[var(--fop-primary-soft)]"
      }`}
      data-testid={testid}
    >
      <Sunrise
        size={20}
        className={allClear ? "text-[var(--fop-text-3)]" : "text-[var(--fop-primary)]"}
      />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          Morning roll call
        </div>
        <div
          className="text-sm font-semibold text-[var(--fop-text)] truncate"
          data-testid={`${testid}-headline`}
        >
          {data.headline}
        </div>
      </div>
      {!allClear && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`${testid}-chips`}>
          {data.critical_changes > 0 && (
            <ImpactChip severity="critical" count={data.critical_changes} testid={`${testid}-critical`} />
          )}
          {data.high_changes > 0 && (
            <ImpactChip severity="high" count={data.high_changes} testid={`${testid}-high`} />
          )}
          {data.field_conflicts_open > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
              style={{
                color: "#fecaca",
                border: "1px solid rgba(220,38,38,0.6)",
                background: "rgba(220,38,38,0.18)",
              }}
              data-testid={`${testid}-conflict`}
            >
              {data.field_conflicts_open} field conflict{data.field_conflicts_open !== 1 ? "s" : ""}
            </span>
          )}
          {data.clarifications_open > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
              style={{
                color: "#fde68a",
                border: "1px solid rgba(234,179,8,0.6)",
                background: "rgba(234,179,8,0.16)",
              }}
              data-testid={`${testid}-clarification`}
            >
              {data.clarifications_open} clarification{data.clarifications_open !== 1 ? "s" : ""}
            </span>
          )}
          {data.foremen_pending > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider"
              style={{
                color: "var(--fop-warning)",
                border: "1px solid var(--fop-warning)",
                background: "rgba(245, 158, 11, 0.12)",
              }}
              data-testid={`${testid}-pending`}
            >
              {data.foremen_pending} pending
            </span>
          )}
        </div>
      )}
      <ChevronRight size={16} className="text-[var(--fop-text-3)] shrink-0" />
    </Link>
  );
}
